import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";

import { CreateSupplicationRequest, ParsedError } from "../../interfaces";

import { ActivityButton, AppRichEditor } from "../../components/Common";
import {
  useAddSupplicationMutation,
  useLazyGetSupplicationQuery,
  useUpdateSupplicationMutation,
} from "../../store/api/supplicationApi";
import { ErrorHandlingTypeEnum } from "../../enums";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const AddEditSupplicationModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
}) => {
  const { t } = useTranslation();
  const [request] = useState<CreateSupplicationRequest>();

  const [
    getSupplicationApi,
    {
      isLoading: isLoadingSupplication,
      isSuccess: isSuccessSupplication,
      data: dataSupplication,
      error: errorSupplication,
      isError: isErrorSupplication,
    },
  ] = useLazyGetSupplicationQuery();

  const [
    updateSupplicationApi,
    {
      isLoading: isLoadingUpdate,
      isSuccess: isSuccessUpdate,
      error: errorUpdate,
      isError: isErrorUpdate,
    },
  ] = useUpdateSupplicationMutation();

  const [
    addSupplicationApi,
    {
      isLoading: isLoadingAdd,
      isSuccess: isSuccessAdd,
      error: errorAdd,
      isError: isErrorAdd,
    },
  ] = useAddSupplicationMutation();

  //#region form
  const form = useFormik<CreateSupplicationRequest>({
    enableReinitialize: true,
    initialValues: {
      name: {
        en: request?.name.en || "",
        ar: request?.name.ar || "",
      },
      content: {
        en: request?.name.en || "",
        ar: request?.name.ar || "",
      },
    },
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "arabicValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      content: Yup.object({
        en: Yup.string().required(
          t("isNotEmpty", {
            displayName: "englishValue",
          }),
        ),
        ar: Yup.string().required(
          t("isNotEmpty", {
            displayName: "arabicValue",
          }),
        ),
      }),
    }),
    onSubmit: (values) => {
      if (id) {
        updateSupplicationApi({ id: id, data: values });
      } else {
        addSupplicationApi(values);
      }
    },
  });
  //#endregion

  //#region effects
  useEffect(() => {
    if (
      (!isLoadingSupplication && isSuccessSupplication) ||
      (!isLoadingUpdate && isSuccessUpdate) ||
      (!isLoadingAdd && isSuccessAdd)
    ) {
      onClose();
    }
  }, [
    isLoadingSupplication,
    isSuccessSupplication,
    isLoadingUpdate,
    isSuccessUpdate,
    isLoadingAdd,
    isSuccessAdd,
  ]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingAdd, isLoadingUpdate]);

  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
  };

  useEffect(() => {
    if (id) {
      getSupplicationApi(id);
    }
  }, [id]);

  useEffect(() => {
    if (id && dataSupplication) {
      form.setValues({
        name: dataSupplication.name,
        content: dataSupplication.content,
      });
    }
  }, [dataSupplication, id, isLoadingSupplication]);

  //#endregion

  return (
    <Modal
      id="addEdit"
      isOpen={show}
      toggle={() => {
        onClose();
      }}
      centered
      className="modal-lg"
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalHeader
          className="p-3"
          toggle={() => {
            onClose();
          }}
        >
          {id
            ? t("editItem", { item: "supplication" })
            : t("addItem", { item: "supplication" })}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label htmlFor="name.en" className="form-label">
              {t("nameEn")}
              <span className="text-danger">*</span>
            </label>
            <Input
              disabled={isErrorSupplication}
              autoComplete="off"
              auto
              id="name.en"
              name="name.en"
              value={form.values.name?.en}
              type="text"
              className="form-control"
              placeholder={t("enterInEnglish")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.name?.en && form.errors.name?.en ? true : false
              }
            />
            {form.touched.name?.en && form.errors.name?.en ? (
              <FormFeedback type="invalid">
                {form.errors.name?.en as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label htmlFor="name.ar" className="form-label">
              {t("nameAr")}
              <span className="text-danger">*</span>
            </label>
            <Input
              style={{ fontFamily: "aljannat", direction: "rtl" }}
              disabled={isErrorSupplication}
              autoComplete="off"
              auto
              id="name.ar"
              name="name.ar"
              value={form.values.name?.ar}
              type="text"
              className="form-control"
              placeholder={t("enterInArabic")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.name?.ar && form.errors.name?.ar ? true : false
              }
            />
            {form.touched.name?.ar && form.errors.name?.ar ? (
              <FormFeedback type="invalid">
                {form.errors.name?.ar as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <Label htmlFor="content.en" className="form-label">
              {t("contentEn")}
              <span className="text-danger">*</span>
            </Label>
            <AppRichEditor
              initialValue={form.values?.content?.en || ""}
              onChange={(data: any) => {
                form.setFieldValue("content.en", data);
              }}
              onKeyUp={(data: any) => {
                if (data.trim().length == 0) {
                  form.setFieldError(
                    "content.en",
                    t("isNotEmpty", {
                      displayName: "englishValue",
                    }),
                  );
                } else {
                  form.setFieldError("content.en", "");
                }
              }}
              onFocus={() => {
                form.setFieldTouched("content.en", true, true);
              }}
            />
            {form.touched.content?.en && form.errors.content?.en ? (
              <Label className="invalid-feedback d-block">
                {form.errors.content?.en as string}
              </Label>
            ) : null}
          </div>
          <div className="mb-3">
            <Label htmlFor="content.ar" className="form-label">
              {t("contentAr")}
              <span className="text-danger">*</span>
            </Label>
            <AppRichEditor
              initialValue={form.values?.content?.ar || ""}
              onChange={(data: any) => {
                form.setFieldValue("content.ar", data);
              }}
              onKeyUp={(data: any) => {
                if (data.trim().length == 0) {
                  form.setFieldError(
                    "content.ar",
                    t("isNotEmpty", {
                      displayName: "arabicValue",
                    }),
                  );
                } else {
                  form.setFieldError("content.ar", "");
                }
              }}
              onFocus={() => {
                form.setFieldTouched("content.ar", true, true);
              }}
            />
            {form.touched.content?.ar && form.errors.content?.ar ? (
              <Label className="invalid-feedback d-block">
                {form.errors.content?.ar as string}
              </Label>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onClose}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title="save"
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            inProgress={
              isLoadingSupplication || isLoadingAdd || isLoadingUpdate
            }
            disabled={isLoadingSupplication || isLoadingAdd || isLoadingUpdate}
          ></ActivityButton>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
